/*
 * Rewriting a reply without deleting the thread under it.
 *
 * Body.setAsync replaces the WHOLE compose body, and in a reply the whole body
 * includes the quoted chain Outlook put below the cursor. So a "replace" rewrite
 * has to be a read-modify-write: keep the document head (classic Outlook hands
 * back a full <html> document) and everything from the start of the quoted chain
 * down, and swap only what sits between them.
 *
 * Every client marks the chain differently. The patterns below were read off real
 * bodies from each client; the fixtures in quoted-chain.test.ts copy their structure.
 */

// Classic Outlook returns <html><head>…<body><div class=WordSection1>…; the chain
// sits INSIDE that wrapper, so the wrapper's opening tag stays with the head.
const HEAD_RE = /^[\s\S]*?<body[^>]*>(?:\s*<div[^>]*class=["']?WordSection\d*["']?[^>]*>)?/i;

// Each pattern matches where the quoted chain STARTS. The earliest match wins.
const CHAIN_START: RegExp[] = [
  // Outlook on the web compose: an empty marker div right above the separator.
  /<div[^>]*\bid=["']?appendonsend\b[^>]*>/i,
  // Outlook on the web / Graph / mobile: an <hr> (sometimes wrapped in its own div)
  // immediately followed by the From/Sent header block. A bare <hr> is NOT enough —
  // signatures carry them too.
  /(?:<div[^>]*>\s*)?<hr[^>]*>\s*(?:<\/div>\s*)?(?=<div[^>]*\bid=["']?(?:x_)*divRplyFwdMsg\b)/i,
  /<div[^>]*\bid=["']?(?:x_)*divRplyFwdMsg\b[^>]*>/i,
  // New Outlook / Outlook for Mac.
  /<div[^>]*\bid=["']?(?:x_)*mail-editor-reference-message-container\b[^>]*>/i,
  // Classic Outlook for Windows: a border-top div whose first text is the From: label.
  /<div[^>]*border-top:\s*solid[^>]*>(?=(?:\s|&nbsp;|<(?!\/?div\b)[^>]*>)*(?:From|De|Von|Van|Da)\s*:)/i,
  // Non-Outlook clients quoted into the thread.
  /<div[^>]*class=["']?gmail_quote\b[^>]*>/i,
];

const ORIGINAL_MESSAGE_RE = /-{3,}\s*Original Message\s*-{3,}/i;

// A header block we could not place: better to refuse than to guess and wipe it.
const LOOKS_LIKE_CHAIN_RE =
  /\b(?:From|De|Von)\s*:[\s\S]{0,300}?\b(?:Sent|Date|Gesendet|Envoyé)\s*:/i;

function chainStart(rest: string): number {
  let best = -1;
  for (let i = 0; i < CHAIN_START.length; i++) {
    const m = CHAIN_START[i].exec(rest);
    if (m && (best < 0 || m.index < best)) best = m.index;
  }

  const om = ORIGINAL_MESSAGE_RE.exec(rest);
  if (om && (best < 0 || om.index < best)) {
    // Start from the block that holds the separator, not mid-paragraph.
    const before = rest.slice(0, om.index);
    const block = Math.max(before.lastIndexOf("<p"), before.lastIndexOf("<div"));
    best = block >= 0 ? block : om.index;
  }
  if (best < 0) return -1;

  // Classic Outlook nests the header in a bare <div>; leaving that opener behind
  // would strand its closing tag inside the chain.
  let m: RegExpExecArray | null;
  while ((m = /<div>\s*$/i.exec(rest.slice(0, best))) !== null) {
    best = m.index;
  }
  return best;
}

/**
 * The body with everything above the quoted chain replaced by `newHtml`.
 * Returns `newHtml` alone when the body has no chain (a new message), and
 * `null` when the body looks like it carries a chain we cannot locate — the
 * caller must then leave the body untouched rather than wipe the history.
 */
export function replaceAboveQuotedChain(bodyHtml: string, newHtml: string): string | null {
  const html = bodyHtml || "";
  const headMatch = HEAD_RE.exec(html);
  const head = headMatch ? headMatch[0] : "";
  const rest = html.slice(head.length);

  const start = chainStart(rest);
  if (start >= 0) return head + newHtml + rest.slice(start);

  const text = rest.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ");
  if (LOOKS_LIKE_CHAIN_RE.test(text)) return null;
  return newHtml;
}

function normalizeConversationId(id: string | null | undefined): string {
  if (!id) return "";
  return String(id).trim().replace(/-/g, "+").replace(/_/g, "/").replace(/=+$/, "");
}

/** Graph and Office.js can hand back the same conversation id in standard or URL-safe base64. */
export function sameConversation(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const x = normalizeConversationId(a);
  return x !== "" && x === normalizeConversationId(b);
}
